import React, { useEffect } from "react";
import {
  Button,
  Modal,
  Box,
  SpaceBetween,
} from "@amzn/awsui-components-react-v3";
import {
  FieldSchemaDto,
  FieldGroupDto,
} from "@amzn/crowd-front-end-common-library";
import { TeamDtoWithBreadcrumb } from "../../layout/subheader/SubHeaderDto";
import { MetricsState } from "../../logging";
import addUserFormSchema from "./addUserFormSchema";
import { useFormDispatch } from "../../forms/formContext";
import { actions } from "../../forms/formSlice";
import FormFieldRenderer from "../../forms/FormFieldRenderer/FormFieldRenderer";
import styles from "../permissions_page.css";
import { AutoSuggestItem } from "../../search/AutoSuggestResponse";
import { useHttpRequest, LoadingState } from "../../http";
import { changesAfterFirstFailureBanner } from "../../common/utils/ErrorBannerMessages";
import { DisplayBanner } from "../../banner/bannerSlice";

interface AddUserModalProps {
  team: TeamDtoWithBreadcrumb;
  metrics: MetricsState;
  localChanges: Partial<AddUserResult>;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  reloadPermissionsTable: () => void;
  setBanner: (banner: DisplayBanner | undefined) => void;
}

export interface AddUserResult {
  admins: AutoSuggestItem[];
}

const extractEmployeeNameFromTitle = (item: AutoSuggestItem): string => {
  if (!item || !item.title) {
    return "";
  }
  const { title } = item;
  return title.substring(0, title.indexOf("("));
};

const getSuccessfulBannerMessage = (
  admins: AutoSuggestItem[],
  responseStatus: number,
  teamName: string
): string => {
  const bannerMsg =
    admins.length > 1
      ? `${admins.length} users have been added as admins`
      : `${extractEmployeeNameFromTitle(admins[0])} has been added as an admin`;

  const pendingResultsMessage =
    responseStatus === 200
      ? ""
      : "  This change may take a while to appear in your team.";

  return `${bannerMsg} of ${teamName}.${pendingResultsMessage}`;
};

const AddUserModal: React.FC<AddUserModalProps> = ({
  team,
  metrics,
  localChanges,
  showModal,
  setShowModal,
  setBanner,
  reloadPermissionsTable,
}): JSX.Element => {
  const dispatch = useFormDispatch();

  const fieldGroup = addUserFormSchema.groups.find(
    (group) => group.groupLabel === "Add user"
  ) as FieldGroupDto;
  const adminsField = fieldGroup.fieldDefinitions.find(
    (field) => field.propertyName === "admins"
  ) as FieldSchemaDto;

  const getAdminsFieldLabel = (
    <div>
      <div>{`${team.name} admins`}</div>
      <div className={styles["info-text"]}>
        Add additional admins using the search input below.
      </div>
    </div>
  );

  const clearSelection = (): void => {
    dispatch(
      actions.setPropertyValueAction({
        propertyName: "admins",
        value: [],
      })
    );
  };

  const {
    loadingState: addUserLoadingState,
    sendRequest: addUserSendRequest,
    error: addUserError,
    response: addUserAxiosResponse,
  } = useHttpRequest("addTeamAdmins", metrics);

  useEffect(() => {
    if (addUserLoadingState === LoadingState.RESOLVED && addUserAxiosResponse) {
      const admins = localChanges.admins as AutoSuggestItem[];
      setBanner({
        type: "success",
        content: getSuccessfulBannerMessage(
          admins,
          addUserAxiosResponse.status,
          team.name
        ),
        dissmissible: true,
      });
      if (addUserAxiosResponse.status === 200) {
        reloadPermissionsTable();
      }
      clearSelection();
      setShowModal(false);
    }
    if (addUserLoadingState === LoadingState.REJECTED || addUserError) {
      // TODO: handle 404 differently
      setBanner(changesAfterFirstFailureBanner);
      clearSelection();
      setShowModal(false);
    }
  }, [
    addUserLoadingState,
    addUserAxiosResponse,
    addUserError,
    setShowModal,
    setBanner,
    localChanges.admins,
    team.name,
    reloadPermissionsTable,
  ]);

  return (
    <div className={styles["centered-modal"]}>
      <Modal
        header="Add team admin"
        visible={showModal}
        closeAriaLabel="exit modal dismiss button"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                formAction="none"
                onClick={() => {
                  clearSelection();
                  setShowModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                formAction="none"
                loading={addUserLoadingState === LoadingState.LOADING}
                disabled={
                  !localChanges.admins ||
                  (localChanges.admins && localChanges.admins.length === 0)
                }
                onClick={() => {
                  const admins = localChanges.admins
                    ? localChanges.admins.map((admin) =>
                        admin.attributes ? admin.attributes.employeeId : ""
                      )
                    : [];
                  addUserSendRequest({
                    url: `/api/v1/teams/${team.teamId}/admins/add`,
                    method: "POST",
                    data: { teamId: team.teamId, employeeIds: admins },
                  });
                }}
              >
                Save changes
              </Button>
            </SpaceBetween>
          </Box>
        }
        onDismiss={() => {
          clearSelection();
          setShowModal(false);
        }}
      >
        <FormFieldRenderer
          field={adminsField}
          customLabel={getAdminsFieldLabel}
          stretch
        />
      </Modal>
    </div>
  );
};

export default AddUserModal;
